import { capitalize } from "@sp-crm/core";
export interface TextSelection {
    start: number;
    end: number;
}

export interface SplicedText {
    value: string;
    selection: TextSelection;
}

const sentenceBoundaryBefore = /(^|[.!?]|\n)[ \t]*$/;
const noSpaceBeforeText = /^[\s.,!?;:)]/;
const noSpaceAfterText = /^[\s.,!?;:)]/;

const trimEdgeSpaces = (text: string): string =>
    text.replace(/^[ \t]+/, "").replace(/[ \t]+$/, "");

const capitalizeWhenStartingSentence = (before: string, text: string): string => {
    const leadingWhitespace = /^\s*/.exec(text)?.[0] ?? "";
    const body = text.slice(leadingWhitespace.length);
    if (!sentenceBoundaryBefore.test(before + leadingWhitespace)) {
        return text;
    }
    return leadingWhitespace + capitalize(body);
};

const spaceBefore = (before: string, text: string): string =>
    before.length > 0 && !/\s$/.test(before) && !noSpaceBeforeText.test(text) ? " " : "";

const spaceAfter = (text: string, after: string): string =>
    after.length > 0 && !/\s$/.test(text) && !noSpaceAfterText.test(after) ? " " : "";

export const spliceDictatedText = (
    value: string,
    selection: TextSelection,
    dictated: string,
): SplicedText => {
    const trimmed = trimEdgeSpaces(dictated);
    if (trimmed.trim() === "") {
        return { value, selection };
    }
    const before = value.slice(0, selection.start);
    const after = value.slice(selection.end);
    const text = capitalizeWhenStartingSentence(before, trimmed);
    const inserted = spaceBefore(before, text) + text;
    const cursor = before.length + inserted.length;
    return {
        value: before + inserted + spaceAfter(text, after) + after,
        selection: { start: cursor, end: cursor },
    };
};

const spokenMarks: Record<string, string> = {
    "new paragraph": "\n\n",
    "new line": "\n",
    "full stop": ".",
    period: ".",
    comma: ",",
    "question mark": "?",
    "exclamation point": "!",
    "exclamation mark": "!",
};

const spokenMarkPattern = new RegExp(
    `\\s*\\b(${Object.keys(spokenMarks).join("|")})\\b\\s*`,
    "gi",
);

const repeatedMarks = /([.,!?])(?:\s*[.,!?])+/g;

const capitalizeSentenceStarts = (text: string): string =>
    text.replace(
        /([.!?][ \t]+|\n[ \t]*)([a-z])/g,
        (_, separator: string, letter: string) => separator + letter.toUpperCase(),
    );

export const applySpokenPunctuation = (text: string): string => {
    const replaced = text.replace(
        spokenMarkPattern,
        (match: string, word: string, offset: number) => {
            const mark = spokenMarks[word.toLowerCase()];
            const hasMoreText = offset + match.length < text.length;
            return mark.startsWith("\n") || !hasMoreText ? mark : `${mark} `;
        },
    );
    return capitalizeSentenceStarts(replaced.replace(repeatedMarks, "$1"));
};
